import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin, pendingInvitesCount } from "@/lib/guards";
import AccountSwitcher from "@/components/account/AccountSwitcher";
import { SWITCHER } from "@/constants/testIds/profile";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, Users, CalendarDays, BookMarked, Menu, X, UserRound, ShieldCheck, ChevronDown, Sparkles } from "lucide-react";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    nav("/");
  };

  const pendingInvites = pendingInvitesCount(user);
  // Keep the BrandPersona menu useful during both onboarding steps. `/marca`
  // is a completed-app surface and would otherwise bounce an unfinished avatar
  // straight back to the avatar flow.
  const personaPath = brandPersonaPath(user);

  return (
    <header className="sticky top-0 z-40 bg-brand-bg/70 backdrop-blur-xl border-b border-brand-line">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-2 min-w-0">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center shrink-0" data-testid="header-logo">
          <BrandLogo size="md" />
        </Link>

        {user ? (
          <>
            <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 p-1 rounded-full bg-brand-surface/70 border border-brand-line backdrop-blur-md min-w-0">
              <HeaderLink to="/dashboard" icon={LayoutDashboard} label="Painel" testid="nav-dashboard" loc={loc} />
              <HeaderLink to={personaPath} icon={Sparkles} label="BrandPersona" testid="nav-marca" loc={loc} />
              <HeaderLink to="/avatars" icon={Users} label="Avatares" testid="nav-avatars" loc={loc} />
              <HeaderLink to="/calendario" icon={CalendarDays} label="Calendário" testid="nav-calendar" loc={loc} />
              <HeaderLink to="/library" icon={BookMarked} label="Biblioteca" testid="nav-library" loc={loc} />
            </nav>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={SWITCHER.trigger}
                  aria-label="Menu da conta"
                  className="relative flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-full border border-brand-line bg-brand-surface/70 hover:border-brand-rose transition-colors"
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-brand-elevated text-brand-rose flex items-center justify-center">
                    <UserRound className="w-4 h-4" />
                  </span>
                  <span className="hidden sm:inline text-[13px] text-brand-ink font-ui max-w-[120px] truncate">
                    {user.name?.split(" ")[0] || "Conta"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-brand-ink2" />
                  {pendingInvites > 0 && (
                    <span
                      data-testid={SWITCHER.pendingDot}
                      aria-label={`${pendingInvites} convite(s) pendente(s)`}
                      className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-brand-rose ring-2 ring-brand-bg"
                    />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-72 bg-brand-surface border-brand-line p-2"
              >
                <div className="px-1 pb-1.5">
                  <AccountSwitcher dense onSwitched={() => setMenuOpen(false)} />
                </div>
                <DropdownMenuSeparator className="bg-brand-line" />
                <DropdownMenuItem asChild className="focus:bg-brand-elevated cursor-pointer rounded-xl">
                  <Link to={personaPath} data-testid="header-marca-link" className="flex items-center gap-2 text-brand-ink py-2.5">
                    <Sparkles className="w-4 h-4 text-brand-rose" />
                    <span className="flex-1 text-[13.5px] font-ui">Minha BrandPersona</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-brand-elevated cursor-pointer rounded-xl">
                  <Link to="/perfil" data-testid="header-profile-link" className="flex items-center gap-2 text-brand-ink py-2.5">
                    <UserRound className="w-4 h-4 text-brand-rose" />
                    <span className="flex-1 text-[13.5px] font-ui">Minha conta</span>
                    {pendingInvites > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-brand-rose/15 text-brand-rose text-[10.5px] font-ui">
                        {pendingInvites}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                {isAdmin(user) && (
                  <DropdownMenuItem asChild className="focus:bg-brand-elevated cursor-pointer rounded-xl">
                    <Link to="/admin" data-testid="header-admin-link" className="flex items-center gap-2 text-brand-ink py-2.5">
                      <ShieldCheck className="w-4 h-4 text-brand-rose" />
                      <span className="text-[13.5px] font-ui">Painel admin</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="focus:bg-brand-elevated cursor-pointer rounded-xl text-brand-ink py-2.5"
                >
                  <LogOut className="w-4 h-4 mr-2 text-brand-rose" />
                  <span className="text-[13.5px] font-ui">Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <>
            {/* Inline pill nav for marketing */}
            <nav className="hidden md:flex items-center gap-1 p-1 rounded-full bg-brand-surface/70 border border-brand-line backdrop-blur-md">
              <a href="/#features" className="px-5 py-1.5 text-[12.5px] font-ui text-brand-ink2 hover:text-white transition-colors">Recursos</a>
              <a href="/#how" className="px-5 py-1.5 text-[12.5px] font-ui text-brand-ink2 hover:text-white transition-colors">Como funciona</a>
            </nav>
            <div className="hidden md:flex items-center gap-2">
              <Link
                data-testid="nav-login"
                to="/login"
                className="px-4 py-2 rounded-full text-[12.5px] font-ui text-brand-ink2 hover:text-white transition-colors"
              >
                Entrar
              </Link>
              <Link
                data-testid="nav-signup"
                to="/signup"
                className="pill-pink px-5 py-2 text-[12.5px] font-ui"
              >
                Começar
              </Link>
            </div>
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </>
        )}
      </div>

      {/* mobile drawer */}
      {!user && open && (
        <div className="md:hidden border-t border-brand-line bg-brand-surface/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-5 space-y-3">
            <a href="/#features" onClick={() => setOpen(false)} className="block py-1 text-white">Recursos</a>
            <a href="/#how" onClick={() => setOpen(false)} className="block py-1 text-white">Como funciona</a>
            <div className="pt-3 border-t border-brand-line flex gap-2">
              <Link to="/login" onClick={() => setOpen(false)} className="pill-ghost px-5 py-2 text-[13px] font-ui inline-flex items-center">Entrar</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="pill-pink px-5 py-2 text-[13px] font-ui inline-flex items-center">Começar</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// The compact account dropdown is the navigation available on mobile during
// onboarding, so it must use the same destination as the desktop BrandPersona
// link instead of routing an unfinished Avatar through `/marca`.
export function brandPersonaPath(user) {
  return user?.status_onboarding === "concluido"
    ? "/marca"
    : "/onboarding/brand-persona";
}

function HeaderLink({ to, icon: Icon, label, testid, loc }) {
  const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
  return (
    <Link
      data-testid={testid}
      to={to}
      title={label}
      aria-label={label}
      className={`flex items-center gap-2 px-2.5 lg:px-4 py-1.5 rounded-full text-[12.5px] tracking-wide transition-colors ${
        active
          ? "bg-brand-rose text-brand-bg font-semibold"
          : "text-brand-ink2 hover:text-white"
      }`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="font-ui hidden lg:inline">{label}</span>
    </Link>
  );
}
